import { useEffect, useRef } from 'react';
import { normalizePublicImageUrl } from '../utils/imageUrl';

/** Minimal card shape — compatible with both Card (types.ts) and LessonCard (newtypes.ts) */
interface PrefetchableCard {
  id: string;
  type: string;
  content: any;
}

/**
 * Extracts all image URIs from a lesson's cards and prefetches them
 * using `new Image()` so the browser cache is primed before the user
 * navigates to each card.
 *
 * Memoized on the first card's ID to avoid re-running on re-renders.
 * Videos are intentionally excluded (signed URLs, large files).
 */
export function useImagePrefetch(cards: PrefetchableCard[] | undefined) {
  const prefetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!cards || cards.length === 0) return;

    // Use first card ID as a stable key to avoid re-running
    const key = cards[0]?.id;
    if (!key || prefetchedRef.current === key) return;
    prefetchedRef.current = key;

    const urls = extractImageUrls(cards);
    for (const url of urls) {
      const img = new Image();
      img.src = url;
    }
  }, [cards]);
}

export function extractImageUrls(cards: PrefetchableCard[]): string[] {
  const seen = new Set<string>();

  for (const card of cards) {
    const c = card.content;
    if (!c) continue;

    switch (card.type) {
      case 'information':
      case 'learning_outcomes':
      case 'image_hotspot':
      case 'poll':
        addUrl(seen, c.image_uri);
        break;

      case 'flashcard':
        if (Array.isArray(c.items)) {
          for (const item of c.items) {
            addUrl(seen, item.front_image_uri);
            addUrl(seen, item.back_image_uri);
          }
        }
        break;

      case 'reveal_text_strip':
        if (Array.isArray(c.items)) {
          for (const item of c.items) {
            addUrl(seen, item.image_uri);
            addUrl(seen, item.icon_uri);
          }
        }
        break;

      case 'match_exercise':
        addUrl(seen, c.image_uri);
        break;

      default:
        break;
    }
  }

  return Array.from(seen);
}

function addUrl(seen: Set<string>, uri: string | null | undefined) {
  if (!uri) return;
  const normalized = normalizePublicImageUrl(uri);
  if (normalized && !seen.has(normalized)) {
    seen.add(normalized);
  }
}
